import { useEffect, useRef, useState } from "react";
import { motion, useReducedMotion, useScroll, useTransform } from "framer-motion";
import { assets, squad } from "../../data/sep26Data";

const Card = ({ p }) => {
  const staff = p.number === "DT";
  const gk = p.role === "Portero";
  return (
    <li
      className={`squad-card ${staff ? "squad-card--staff" : ""}`}
      data-testid={`s26-jugador-${p.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
    >
      {p.photo ? (
        <>
          <img
            src={p.photo}
            alt={`${p.name}${staff ? ", director técnico" : `, número ${p.number}`}`}
            className="squad-photo absolute inset-0 h-full w-full object-cover object-top"
            draggable="false"
          />
          <div
            className="absolute inset-0"
            style={{ background: "linear-gradient(0deg, rgba(7,10,28,.96) 0%, rgba(7,10,28,.55) 34%, transparent 62%)" }}
          />
        </>
      ) : (
        <img
          src={assets.crest}
          alt=""
          aria-hidden="true"
          className="absolute -bottom-8 -right-10 w-[78%] opacity-[0.07]"
          style={{ filter: "grayscale(1) brightness(2)" }}
          loading="lazy"
        />
      )}
      {!(staff && p.photo) && (
        <span className={`squad-num ${p.photo ? "squad-num--photo" : ""}`} aria-hidden="true">{p.number}</span>
      )}
      <div className="absolute inset-x-0 bottom-0 p-6">
        {(staff || gk) && (
          <span className="label mb-2 block" style={{ color: "var(--gold)" }}>{p.role}</span>
        )}
        <span className="display block text-[clamp(34px,3.6vw,50px)]">{p.name}</span>
        {!staff && <span className="sr-only"> número {p.number}</span>}
      </div>
    </li>
  );
};

export const Plantilla26 = () => {
  const reduce = useReducedMotion();
  const sectionRef = useRef(null);
  const trackRef = useRef(null);
  const [dist, setDist] = useState(0);
  const distRef = useRef(0);

  useEffect(() => {
    const measure = () => {
      if (!trackRef.current) return;
      const d = Math.max(0, trackRef.current.scrollWidth - window.innerWidth);
      distRef.current = d;
      setDist(d);
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(trackRef.current);
    window.addEventListener("resize", measure);
    return () => {
      ro.disconnect();
      window.removeEventListener("resize", measure);
    };
  }, []);

  const { scrollYProgress } = useScroll({ target: sectionRef, offset: ["start start", "end end"] });
  // función (no arrays) para leer siempre la distancia medida más reciente
  const x = useTransform(scrollYProgress, (v) => {
    const t = Math.min(1, Math.max(0, (v - 0.03) / 0.92));
    return -distRef.current * t;
  });
  const bar = useTransform(scrollYProgress, [0.03, 0.95], [0, 1]);

  const pinned = !reduce;

  return (
    <section
      id="plantilla"
      ref={sectionRef}
      data-testid="s26-plantilla"
      style={pinned ? { height: `calc(100svh + ${dist}px)` } : undefined}
    >
      <div
        className={pinned ? "sticky top-0 flex h-[100svh] flex-col justify-center overflow-hidden" : "py-20"}
      >
        <div className="wrap mb-8 flex items-end justify-between gap-6">
          <h2 className="display text-[clamp(52px,9vw,128px)]">
            La <span style={{ color: "var(--gold)" }}>plantilla</span>
          </h2>
          <p className="serif hidden max-w-[24ch] text-xl sm:block" style={{ color: "var(--muted)" }}>
            Catorce jugadores, un portero y quien los dirige.
          </p>
        </div>

        <div className={pinned ? "" : "overflow-x-auto pb-6"}>
          <motion.ul
            ref={trackRef}
            className="flex gap-4 px-[var(--pad)]"
            style={pinned ? { x } : undefined}
          >
            {squad.map((p) => (
              <Card key={`${p.name}-${p.number}`} p={p} />
            ))}
            {/* el padding final de un flex desbordado no cuenta en scrollWidth */}
            <li aria-hidden="true" className="w-[var(--pad)] shrink-0" />
          </motion.ul>
        </div>

        {pinned && (
          <div className="wrap mt-8" aria-hidden="true">
            <div className="h-px w-full" style={{ background: "var(--line)" }}>
              <motion.div className="h-px origin-left" style={{ scaleX: bar, background: "var(--gold)" }} />
            </div>
          </div>
        )}
      </div>
    </section>
  );
};
